import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import debounce from 'lodash/debounce';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import Tribute from 'tributejs';

import {type ReactRef} from 'app/general/types';
import {showNotification} from 'app/toasts/slice';
import {useUserPermissions} from 'app/user/api';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {UserPermissionsSerializerType} from 'app/current-user/types';
import type {CommentContentType} from 'sweet-types';

type Props = {
  contentType: CommentContentType;
  objectId: string | number;
  onChange: (text: string) => void;
  onSubmit: (
    event: React.KeyboardEvent<HTMLDivElement>,
    currentComment?: string
  ) => void;
  value: string;
  children: (props: {
    Field: typeof ContentEditable;
    fieldProps: {
      ref: {
        current: HTMLElement | null;
      };
      value: string;
      onChange: (content: string) => void;
      onSubmit: (
        event: React.KeyboardEvent<HTMLDivElement>,
        currentComment?: string
      ) => void;
    };
    mentionsMenu: ReactElement<typeof StyledMenuDiv>;
  }) => JSX.Element;
};
export function Mentionable({
  children,
  value,
  onChange,
  onSubmit,
  contentType,
  objectId,
}: Props) {
  const actions = useAsyncActions({
    showNotification,
  });
  const [menuNode, setMenuNode] = useState<null | HTMLElement>(null);
  const tribute = useRef<Tribute<UserPermissionsSerializerType>>();
  const inputRef = useRef<null | HTMLElement>(null);

  const [search, setSearch] = useState<string | undefined>(undefined);
  const tributeCallback = useRef<
    ((perms: Array<UserPermissionsSerializerType>) => void) | null
  >(null);

  const {currentData} = useUserPermissions(
    {
      object_id: objectId,
      content_type: contentType,
      page_size: '10',
      search,
      with_permissions: search === '' ? 1 : undefined,
    },
    {skip: search == null}
  );

  useEffect(() => {
    if (currentData != null && tributeCallback.current != null) {
      tributeCallback.current(currentData.results);
    }
  }, [currentData, tributeCallback.current]);

  const debouncedSearch = useCallback(
    debounce(
      (
        search: string,
        callback: (perms: Array<UserPermissionsSerializerType>) => void
      ) => {
        setSearch(search);
        tributeCallback.current = callback;
      },
      500
    ),
    []
  );

  useEffect(() => {
    if (menuNode != null && inputRef.current != null) {
      tribute.current = new Tribute<UserPermissionsSerializerType>({
        values: debouncedSearch,
        positionMenu: false,
        lookup: 'name',
        fillAttr: 'name',
        menuContainer: menuNode,
        menuItemTemplate: ({original: {name, email, can_view}}) =>
          ReactDOMServer.renderToStaticMarkup(
            <div className={cs({disabled: !can_view})}>{name || email}</div>
          ),
        selectTemplate: (item: {original: UserPermissionsSerializerType}) => {
          const {
            original: {name, email, id, can_view},
          } = item;

          if (!can_view) {
            actions.showNotification(
              gt(
                'That teammate cannot receive a mention because they do not have access to this document'
              ),
              'warning'
            );
          }

          return can_view
            ? ReactDOMServer.renderToStaticMarkup(
                <a data-user-id={id} contentEditable={false}>
                  @{name || email}
                </a>
              )
            : '';
        },
      });
      tribute.current.attach(inputRef.current);
    }
  }, [menuNode, inputRef.current]);
  return children({
    Field: ContentEditable,
    fieldProps: {
      ref: inputRef,
      value,
      onChange,
      onSubmit,
    },
    mentionsMenu: <StyledMenuDiv ref={(node) => setMenuNode(node)} />,
  });
}
type ContentEditableProps = {
  onChange: (content: string) => void;
  value: string;
  disabled?: boolean;
  forwardedRef: any;
  onSubmit: (
    event: React.KeyboardEvent<HTMLDivElement>,
    currentComment?: string
  ) => void;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
  style?: Record<string, any>;
};

const BaseContentEditable = ({
  value,
  onChange,
  forwardedRef,
  onSubmit,
  autoFocus,
  className,
  placeholder,
  style,
  disabled = false,
}: ContentEditableProps) => {
  const divRef = React.createRef<HTMLDivElement>();
  const cachedValue = useRef(value);

  const [focused, setFocused] = useState(false);

  function getCurrentHTML() {
    if (!divRef.current) {
      return '';
    }
    const html = divRef.current.innerHTML.trim();
    return html === '<br>' ? '' : html;
  }

  useEffect(() => {
    if (typeof forwardedRef === 'function') {
      forwardedRef(divRef.current);
    } else {
      forwardedRef.current = divRef.current;
    }

    if (autoFocus === true) {
      setTimeout(() => {
        if (divRef.current != null && !focused) {
          divRef.current.focus();
          setFocused(true);
        }
      }, 0);
    }
  }, [autoFocus, forwardedRef]);

  useEffect(() => {
    if (divRef.current != null && getCurrentHTML() !== value) {
      divRef.current.innerHTML = value;
    }
  }, [value]);

  function interceptEnter(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !disabled) {
      event.preventDefault();
      const currentHTML = getCurrentHTML();
      if (currentHTML !== '') {
        onSubmit(event, currentHTML);
      }
    }
  }
  function handleKeydown(event: React.KeyboardEvent<HTMLDivElement>) {
    interceptEnter(event);
    const targetNode = interceptBackspace(event, divRef);
    if (targetNode) {
      event.preventDefault();
      targetNode.remove();
      emitChange();
    }
  }

  function emitChange() {
    if (!divRef.current) {
      return;
    }

    const newValue = getCurrentHTML();

    if (newValue === '') {
      divRef.current.innerHTML = newValue;
    }
    if (newValue !== value) {
      onChange(newValue);
    }
  }

  return (
    <StyledWithMentionsDiv
      onSubmit={onSubmit}
      className={cs(className, {
        'disabled list-group-item-light opacity-50': disabled,
      })}
      placeholder={placeholder}
      style={style}
      ref={divRef}
      onInput={emitChange}
      onBlur={emitChange}
      onKeyDown={handleKeydown}
      contentEditable={!disabled}
      dangerouslySetInnerHTML={{
        __html: cachedValue.current,
      }}
    />
  );
};

export const ContentEditable = React.forwardRef<
  HTMLDivElement,
  Omit<ContentEditableProps, 'forwardedRef'>
>((props, ref) => <BaseContentEditable {...props} forwardedRef={ref} />);

function findMentionNode(node: Node | null): HTMLElement | null {
  if (!node) return null;
  if (
    node.nodeType === Node.ELEMENT_NODE &&
    (node as HTMLElement).tagName === 'A' &&
    (node as HTMLElement).dataset.userId
  ) {
    return node as HTMLElement;
  }
  return node.parentElement && findMentionNode(node.parentElement);
}

function interceptBackspace(
  event: React.KeyboardEvent<HTMLDivElement>,
  divRef: ReactRef<HTMLDivElement>
) {
  if (event.key !== 'Backspace' || !divRef.current) {
    return;
  }

  const selection = window.getSelection();
  if (selection?.anchorNode == null) {
    return;
  }

  const {anchorNode, anchorOffset} = selection;

  let targetNode: HTMLElement | null = null;

  if (anchorNode.nodeType === Node.TEXT_NODE) {
    // Cursor inside text or at end, check parent for mention
    if (anchorOffset !== 0) {
      targetNode = findMentionNode(anchorNode);
    }
    // Cursor at text end, check next sibling for mention
    else if (
      anchorOffset === anchorNode.textContent?.length &&
      anchorNode.nextSibling
    ) {
      targetNode = findMentionNode(anchorNode.nextSibling);
    }
  } else if (anchorNode.nodeType === Node.ELEMENT_NODE) {
    const childNodes = Array.from(anchorNode.childNodes);
    targetNode = findMentionNode(childNodes[anchorOffset]);
    if (!targetNode && anchorOffset > 0) {
      targetNode = findMentionNode(childNodes[anchorOffset - 1]);
    }
  }

  return targetNode;
}

export const StyledWithMentionsDiv = styled.div`
  a[data-user-id] {
    &,
    &:hover {
      padding: 2px 0;
      margin: -2px 0;
      background: ${colors.paleBlue};
      color: ${colors.linkBlue};
    }
  }
  &:empty::before {
    content: attr(placeholder);
    color: #999;
    pointer-events: none;
  }
`;
const StyledMenuDiv = styled.div`
  position: relative;

  .tribute-container {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    max-height: 70vh;
    overflow: auto;
    display: block;
    z-index: 5;

    ul {
      margin: 0;
      margin-top: 2px;
      padding: 0;
      list-style: none;
      background: #fff;
      border-top: 1px solid #ccc;

      li {
        cursor: pointer;
        font-size: 90%;

        &.highlight {
          background: ${colors.selectedBg};
          color: #fff;
        }

        &.no-match {
          cursor: default;
        }

        > div {
          padding: 5px 5px;
        }

        &:not(.highlight) > div.disabled {
          color: #999;
        }

        &.highlight > div.disabled {
          background: #999;
        }
      }
    }
  }
`;
